export const WELCOME_HEADING = 'Hi! I’m SpotterCode';
export const WELCOME_MESSAGE =
  'I can help you generate and refine your ThoughtSpot theme using prompts, screenshots, or brand guidelines.';
export const PROMPT_TEMPLATES = [
  'Apply this brand guideline PDF to create a clean, consistent dashboard theme.',
  'Use this screenshot to match the look and feel of my app.',
  'Generate a dark mode theme optimized for analytical dashboards.',
  'Create a minimal theme for an internal analytics application with clear hierarchy.',
];

export const TEXT_AREA_PLACEHOLDER =
  'Describe the theme you want, and/or upload files for reference...';

export const EXPLAINER_CARD_HEADING = 'Add files';
export const EXPLAINER_CARD_MESSAGES = [
  'Upload brand references such as',
  'PDFs and images (JPEG, PNG, WebP, GIF).',
  'More input helps me match your brand better.',
];

export const PDF_WORKER_SRC = 'https://unpkg.com/pdfjs-dist@4.8.69/build/pdf.worker.min.mjs';
export const MAX_PDF_WORDS = 700;
export const MAX_TOTAL_ALLOWED_MB_LIMIT = parseInt(
  process.env.REACT_APP_MAX_TOTAL_ALLOWED_MB_LIMIT || '5'
);

export const POPUP_CONTENT = {
  ROUTE_CHANGE: {
    dismissText: 'Cancel',
    confirmText: 'Leave',
    title: 'Do you want to leave?',
    para: 'Placeholder tell users that leaving will cause all work to be lost. Click “Export JSON” to download JSON file.',
  },
  FAILED_UPLOAD: {
    dismissText: '',
    confirmText: 'Done',
    title: 'Some files upload failed',
    para: `You can upload up to ${MAX_TOTAL_ALLOWED_MB_LIMIT}MB in total across all images and PDFs.`,
  },
  '': {},
};

export const AI_RESPONSE_ERRORS = {
  USER_CANCELLED_ISSUE: {
    id: 'user-cancelled-issue',
    heading: 'Task canceled. No changes were applied.',
    para: 'Your request was canceled. Please try again.',
  },
  INTERNAL_SERVER_ISSUE: {
    id: 'internal-server-issue',
    heading: 'Something went wrong.',
    para: 'Something went wrong while generating the theme. Please try again.',
  },
  PROCESSING_ISSUE: {
    id: 'processing-issue',
    heading: 'An issue occurred while processing.',
    para: 'An issue occurred while generating the theme. Please try again.',
  },
  TIMEOUT_ISSUE: {
    id: 'timeout-issue',
    heading: 'This is taking longer than usual.',
    para: 'You can retry or refine your prompt. If the issue persists, please contact support.',
  },
  NETWORK_ISSUE: {
    id: 'network-issue',
    heading: 'Network error.',
    para: 'A network error occurred. Please check your internet connection and try again.',
  },
};

export const OUT_OF_SCOPE_PROMPTS_HEADING = 'I can help you with:';
export const OUT_OF_SCOPE_PROMPTS = [
  'Adjust colors, fonts, and spacing across the app',
  'Apply a specific brand or visual style',
  'Improve contrast and visual hierarchy',
  'Update buttons, headers, and backgrounds',
  'Generate a theme based on uploaded references',
];

export const CARD_DETAILS = {
  title: 'Want more precise control?',
  content: 'For detailed spacing, typography, or component-level tweaks, switch to Manual mode.',
  disableBeacon: true,
};

export const TEXT_AREA_IDS = {
  CHAT_INPUT: 'chat-input-textarea',
  EDITING: 'editing-textarea',
};

export const ARROW_UP_BUTTON_TOOLTIP = 'Please add a message to continue';
export const EDITING_BUTTON_TOOLTIP = 'No changes to apply';
export const EDITING_BUTTON_TOOLTIP_EMPTY = 'Enter a message to apply changes';

export const EXPLAINER_CARD_IMAGES = [
  {
    alt: 'Explainer Card 1',
    src: '/img/ExplainerCard_1.png',
  },
  {
    alt: 'Explainer Card 2',
    src: '/img/ExplainerCard_2.png',
  },
];

export const REMOVE_PDF_TOOLTIP = 'Remove PDF';
export const REMOVE_IMAGE_TOOLTIP = 'Remove Image';

export const RESET_TO_DEFAULT_AI_MODE_MESSAGE = 'Styles reset to default.';
export const RESET_TO_DEFAULT_TEXT = 'Reset to Default';
export const IMPORT_JSON_TEXT = 'Import JSON';
export const EXPORT_JSON_TEXT = 'Export JSON';

export const COPY_JSON_SUCCESS_MESSAGE = 'JSON copied successfully';
export const COPY_JSON_ERROR_MESSAGE = 'Failed to copy JSON.';

export const JSON_RESPONSE_LABEL = 'Generated JSON';

export const RESET_CHAT_HISTORY_TOOLTIP = 'Reset chat';
export const RESET_DISABLED_TOOLTIP = 'Reset is unavailable while a response is in progress';

export const AVATAR_DEFAULT_NAME = 'User';

export const MANUAL_MODE_DISABLED_TOOLTIP = 'Switch blocked during request';

export const MANUAL_STYLE_CHANGED_PROMPT = `
### SYSTEM INSTRUCTION: THEME OVERRIDE
I have manually defined specific CSS theme variables. You must treat these as the **absolute source of truth**.

**Constraints:**
1. **Immutable Values:** Preserve the manual overrides below exactly as-is. Do not recalculate or modify them.
2. **Contextual Consistency:** Update the rest of the theme (colors, shadows, spacing) to remain visually consistent with these overrides.
3. **Integration:** Incorporate the new user requirements provided below into this updated theme.

**[MANUAL OVERRIDES]**
{{manualOverrides}}

**[NEW USER CONTENT]**
{{userContent}}`;

export const AI_MODE_HEADER_TOOLTIP = 'AI mode';
export const MANUAL_MODE_HEADER_TOOLTIP = 'Manual mode';

export const UPLOAD_FILE_INPUT_ID = 'upload-file-input';

export const MANUAL_MODE_ACTIVE_THRESHOLD = 4;

export const COPY_RESET_DELAY = 2000;

export const FOOTER = {
  DISCLAIMER: 'You are interacting with an AI system. Responses should be reviewed.',
  LEARN_MORE_TEXT: 'Learn more',
  LEARN_MORE_LINK: 'https://developers.thoughtspot.com/docs/SpotterCode',
};

export const ANALYZING_TEXT = 'Analyzing...';
